<template>
  <v-card>
    <v-card-title>Invoices</v-card-title>
    <v-card-subtitle>
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search by invoice id or number"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-subtitle>
    <v-data-table
      :headers="headers"
      :items="invoices"
      class="elevation-1"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalAmount"
    >
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>
      <template v-slot:[`item.number`]="{ item }">
        <v-menu
          v-model="item.showInvoiceSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer; text-decoration: underline">
              <span>{{ item.number }}</span>
            </span>
          </template>
          <v-divider></v-divider>
          <v-list dense>
            <v-list-item style="cursor: pointer">
              <v-list-item-title class="font-weight-medium">
                <a
                  :href="`https://dashboard.stripe.com/invoices/${item.id}`"
                  target="_blank"
                  style="text-decoration: none"
                >
                  Open in Stripe 🔗
                </a>
                <v-icon>mdi-open-in-new</v-icon>
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              :to="{ name: 'InvoiceView', params: { id: item.id } }"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Open Invoice
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyInvoiceEntity(item, 'Invoice ID', item.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Invoice ID
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyInvoiceEntity(item, 'Invoice Number', item.number)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Invoice Number
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.data.amountDue`]="{ item }">
        {{ item.amountDue }} {{ item.currency }}
      </template>
      <template v-slot:[`item.files`]="{ item }">
        <template v-if="item.status !== 'draft'">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                v-if="item.id"
                class="ma-2"
                text
                icon
                color="primary"
                @click="downloadInvoice(item.id)"
              >
                <v-icon>picture_as_pdf</v-icon>
              </v-btn>
            </template>
            <span>PDF</span>
          </v-tooltip>
        </template>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              v-if="item.breakdownPdf"
              class="ma-2"
              text
              icon
              color="primary"
              :href="item.breakdownPdf"
              target="_blank"
            >
              <v-icon>description</v-icon>
            </v-btn>
          </template>
          <span>Breakdown</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.id`]="{ item }">
        <router-link
          v-if="item.paid && !item.fullRefunded"
          :to="{ name: 'InvoiceCreditNote', params: { id: item.id } }"
        >
          Issue Credit Note
        </router-link>
      </template>
    </v-data-table>
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>
<script>
import { API } from "../../api";
import { formatDate } from "../../helpers";
import { writeToClipboard } from "../../utils/copyText";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";

export default {
  name: "InvoicesTable",
  components: {
    SuccessSnackbar,
  },
  data() {
    return {
      formatDate,
      loading: false,
      search: "",
      headers: [
        {
          text: "Number",
          value: "number",
          sortable: false,
          width: "150",
        },
        {
          text: "Type",
          value: "type",
          sortable: false,
        },
        {
          text: "Amount",
          align: "start",
          sortable: true,
          value: "data.amountDue",
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
        },
        {
          text: "Created At",
          sortable: true,
          value: "createdAt",
        },
        {
          text: "Description",
          sortable: false,
          value: "description",
        },
        {
          text: "Files",
          sortable: false,
          value: "files",
          width: "200",
        },
      ],
      invoices: [],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      },
      totalAmount: 0,
      showSuccessMessage: false,
      successMessage: "",
      copiedKey: null,
      resetTimer: undefined,
    };
  },
  watch: {
    options: {
      handler: "debounceFetching",
      deep: true,
    },
    search: {
      handler: "debounceFetching",
    },
  },
  computed: {
    searchQuery() {
      const trimmedSearch = this.search.trim();
      return trimmedSearch === "" ? null : trimmedSearch;
    },
  },
  methods: {
    debounceFetching() {
      if (this.timerId) {
        clearTimeout(this.timerId);
      }

      this.timerId = setTimeout(() => {
        this.getInvoices();
      }, 500);
    },
    async getInvoices() {
      this.loading = true;
      this.invoices = [];
      try {
        const invoices = await API.getInvoices({
          sortBy: this.options.sortBy[0],
          order: this.options.sortDesc[0] ? "desc" : "asc",
          page: this.options.page,
          pageSize:
            this.options.itemsPerPage === -1 ? 0 : this.options.itemsPerPage,
          query: this.searchQuery,
        });
        this.totalAmount = invoices.count;
        this.invoices = invoices.data.map((invoice) => {
          const newInvoice = {
            createdAt: new Date(invoice.created * 1000),
            currency: invoice.currency,
            description:
              invoice.priceDescription?.length < 100
                ? invoice.priceDescription
                : `${
                    invoice.type?.charAt(0).toUpperCase() +
                    invoice.type?.slice(1)
                  } Update`,
            total: invoice.total / 100 ?? 0,
            amountDue: invoice.amountDue / 100 ?? 0,
            status: invoice.status,
            pdf: invoice.invoicePdf,
            breakdownPdf: invoice.invoiceBreakdownPdf,
            number: invoice.invoiceNumber,
            id: invoice.stripeInvoiceId,
            type: invoice.type,
            quantity: invoice.quantity,
            refundable: invoice.refundable,
          };

          return newInvoice;
        });
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    async downloadInvoice(id) {
      this.loading = true;

      try {
        const pdfUrl = await API.getInvoicePdf(id);
        this.loading = false;
        window.open(pdfUrl, "_blank");
      } catch (error) {
        this.loading = false;
      }
    },
    async copyToClipboard(label, value, key) {
      try {
        await writeToClipboard(String(value));
        this.copiedKey = key;
        // Show success message
        this.successMessage = `${label} successfully copied to clipboard!`;
        this.showSuccessMessage = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to copy " + label, e);
      } finally {
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => (this.copiedKey = null), 1500);
      }
    },
    copyInvoiceEntity(item, type, value) {
      item.showInvoiceSelector = false;
      this.copyToClipboard(type, value, type);
    },
  },
};
</script>
